/**
 * Pure helpers for building vendor-bill lines from a purchase order.
 *
 * A vendor may invoice what was delivered (basis RECEIVED) or the whole order
 * up front to lock a price (basis ORDERED). Either way the running total billed
 * for a PO line can never exceed max(ordered, received): billing ahead of
 * delivery is allowed, and an over-receipt is billable in full. Only posted
 * (non-draft, non-cancelled) bills count as billed.
 */
import type { PurchaseOrderItem } from '@/features/purchase-orders';
import type { VendorBillBasis } from './types';

export interface PoLineBillingState {
  po_item_id: string;
  item_name: string;
  unit: string;
  ordered: number;
  received: number;
  /** Σ quantity on posted (non-draft, non-cancelled) vendor bills for this line. */
  billed: number;
  unit_cost: number;
  gst_rate: number;
  discount_type: PurchaseOrderItem['discount_type'];
  discount_value: number;
}

export function toBillingState(item: PurchaseOrderItem): PoLineBillingState {
  return {
    po_item_id: item.id,
    item_name: item.product_name,
    unit: item.unit,
    ordered: item.quantity,
    received: item.received_quantity,
    billed: item.billed_quantity ?? 0,
    unit_cost: item.unit_cost,
    gst_rate: item.gst_rate,
    discount_type: item.discount_type,
    discount_value: item.discount_value,
  };
}

/** Quantity still billable for this line whatever the basis (hard cap): max(ordered, received) − billed. */
export function unbilledOrdered(line: PoLineBillingState): number {
  return Math.max(0, round3(Math.max(line.ordered, line.received) - line.billed));
}

/** Delivered but not yet billed. */
export function unbilledReceived(line: PoLineBillingState): number {
  return Math.max(0, round3(line.received - line.billed));
}

/**
 * Default quantity to prefill for a line under the chosen basis. For a specific
 * receipt, `receiptQty` (what that GRN delivered for the line) wins, still capped
 * at what remains billable on the order.
 */
export function defaultBillQty(line: PoLineBillingState, basis: VendorBillBasis, receiptQty?: number): number {
  const cap = unbilledOrdered(line);
  if (basis === 'ORDERED') return cap;
  if (receiptQty != null) return Math.max(0, Math.min(round3(receiptQty), cap));
  return unbilledReceived(line);
}

/** Validation for a typed quantity: over the billable cap → message, else null. */
export function billQtyError(line: PoLineBillingState, qty: number): string | null {
  if (!(qty > 0)) return null;
  const cap = unbilledOrdered(line);
  if (qty > cap + 1e-9) return `Only ${cap} ${line.unit} left to bill`;
  return null;
}

/** True when the bill quantity runs ahead of what was delivered (price-lock case). */
export function billsAheadOfDelivery(line: PoLineBillingState, qty: number): boolean {
  return qty > unbilledReceived(line) + 1e-9;
}

function round3(n: number): number {
  return Math.round(n * 1000) / 1000;
}
